import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Lightning, SignOut } from "@phosphor-icons/react";
import AttentionFeed from "@/components/AttentionFeed";
import { getTabToken, api } from "@/lib/api";

/* v3.2.56 · Route → active-tab token map. Longest-prefix match wins so
   `/admin/users/:id/transactions` still highlights "Users". Keeping this
   as a static list (rather than each page passing `active` manually)
   means new nav links and new routes can never fall out of sync. */
const NAV_ROUTE_MAP = [
  ["/admin/pe-approvals",     "pe-approvals"],
  ["/admin/pe-licenses",      "admin-licenses"],
  ["/admin/pe-analytics",     "admin-analytics"],
  ["/admin/visitors",         "admin-visitors"],
  ["/admin/contact",          "admin-contact"],
  ["/admin/refunds",          "admin-refunds"],
  ["/admin/users",            "admin-users"],
  ["/admin/permissions",      "admin-permissions"],
  ["/admin/vendor-inquiries", "admin-vendors"],
  ["/admin/integrations",     "admin-integrations"],
  ["/studio",                 "studio"],
  ["/deliverables",           "deliverables"],
  ["/inbox",                  "inbox"],
  ["/library",                "library"],
  ["/studies",                "studies"],
  ["/tools",                  "tools"],
  ["/calculators",            "tools"],
  ["/payments",               "payments"],
  ["/pe/payouts",             "payouts"],
  ["/pe/profile",             "pe-profile"],
  ["/exchange",               "exchange"],
  ["/directory",              "directory"],
  ["/jobs",                   "jobs"],
  ["/projects",               "projects"],
  ["/project",                "projects"],
  ["/dashboard",              "dashboard"],
  ["/settings",               "settings"],
];

function activeFromPath(pathname) {
  for (const [prefix, token] of NAV_ROUTE_MAP) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) return token;
  }
  return null;
}

export default function Topbar({ active: activeProp }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const active = activeProp || activeFromPath(pathname);
  const tabSession = !!getTabToken();     // v3.2.2 · this tab uses its own token

  // v3.2.59 · Inbox unread badge. Poll a small summary endpoint every
  // 45 s so the topbar count reflects new messages without a manual
  // refresh. Only fires for PE + client roles (admins get 0 anyway).
  const [inboxUnread, setInboxUnread] = useState(0);
  useEffect(() => {
    if (!user || (user.role !== "pe" && user.role !== "client")) {
      setInboxUnread(0);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/inbox/summary");
        if (!cancelled) setInboxUnread(data.unread_total || 0);
      } catch { /* ignore */ }
    };
    load();
    const iv = setInterval(load, 45000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user]);

  return (
    <header className="border-b border-white/10 bg-[#0A0C10] sticky top-0 z-40">
      <div className="px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          <div className="w-8 h-8 bg-[#00E5FF] flex items-center justify-center">
            <Lightning weight="fill" size={20} className="text-black" />
          </div>
          <div className="leading-none">
            <div className="font-display font-black text-white text-lg tracking-tighter">EEngDeS</div>
            <div className="label-tag text-[10px]">Enhanced Engineering Design Studio</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {user && (
            <>
              <Link
                to="/dashboard"
                className={`text-sm font-medium transition ${active === "dashboard" ? "text-[#00E5FF]" : "text-gray-300 hover:text-white"}`}
                data-testid="nav-dashboard"
              >
                Home
              </Link>
              <Link
                to="/projects"
                className={`text-sm font-medium transition ${active === "projects" ? "text-[#00E5FF]" : "text-gray-300 hover:text-white"}`}
                data-testid="nav-projects"
              >
                Projects
              </Link>
              <Link
                to="/jobs"
                className={`text-sm font-medium transition ${active === "jobs" ? "text-[#00E5FF]" : "text-gray-300 hover:text-white"}`}
                data-testid="nav-jobs"
              >
                Jobs
              </Link>
              <Link
                to="/directory"
                className={`text-sm font-medium transition ${active === "directory" ? "text-[#00E5FF]" : "text-gray-300 hover:text-white"}`}
                data-testid="nav-directory"
              >
                Directory
              </Link>
              {(user.role === "pe" || user.role === "client" || user.role === "admin") && (
                <Link
                  to="/exchange"
                  className={`text-sm font-medium transition ${active === "exchange" ? "text-[#00E5FF]" : "text-gray-300 hover:text-white"}`}
                  data-testid="nav-exchange"
                >
                  Deliverables
                </Link>
              )}
              {(user.role === "pe" || user.role === "client") && (
                <Link
                  to="/inbox"
                  className={`relative text-sm font-medium transition ${active === "inbox" ? "text-[#00E5FF]" : "text-gray-300 hover:text-white"}`}
                  data-testid="nav-inbox"
                >
                  Inbox
                  {inboxUnread > 0 && (
                    <span className="absolute -top-1 -right-3 min-w-[14px] h-[14px] px-1 text-[9px] font-mono flex items-center justify-center bg-[#FFCC00] text-black"
                           data-testid="nav-inbox-badge">
                      {inboxUnread > 99 ? "99+" : inboxUnread}
                    </span>
                  )}
                </Link>
              )}
              {user.role === "pe" && (
                <Link
                  to="/pe/profile"
                  className={`text-sm font-medium transition ${active === "pe-profile" ? "text-[#00E5FF]" : "text-gray-300 hover:text-white"}`}
                  data-testid="nav-pe-profile"
                >
                  Profile
                </Link>
              )}
              {user.role === "pe" && (
                <Link
                  to="/pe/payouts"
                  className={`text-sm font-medium transition ${active === "payouts" ? "text-[#00E5FF]" : "text-gray-300 hover:text-white"}`}
                  data-testid="nav-payouts"
                  title="Connect Stripe to receive milestone payouts"
                >
                  Payouts
                </Link>
              )}
              {(user.role === "client" || user.role === "pe") && (
                <Link
                  to="/payments"
                  className={`text-sm font-medium transition ${active === "payments" ? "text-[#00E5FF]" : "text-gray-300 hover:text-white"}`}
                  data-testid="nav-payments"
                  title={user.role === "pe" ? "Your earnings ledger" : "Your payment history"}
                >
                  {user.role === "pe" ? "Earnings" : "Payments"}
                </Link>
              )}
              {user.role === "admin" && (
                <Link
                  to="/tools"
                  className={`text-sm font-medium transition ${active === "tools" || active === "calculators" ? "text-[#00E5FF]" : "text-gray-300 hover:text-white"}`}
                  data-testid="nav-tools"
                >
                  Tools
                </Link>
              )}
              {user.role === "admin" && (
                <Link
                  to="/studies"
                  className={`text-sm font-medium transition ${active === "studies" ? "text-[#00E5FF]" : "text-gray-300 hover:text-white"}`}
                  data-testid="nav-studies"
                >
                  Studies
                </Link>
              )}
              {user.role === "admin" && (
                <>
                  <Link
                    to="/library"
                    className={`text-sm font-medium transition ${active === "library" ? "text-[#00E5FF]" : "text-gray-300 hover:text-white"}`}
                    data-testid="nav-library"
                  >
                    Library
                  </Link>
                  <Link
                    to="/deliverables"
                    className={`text-sm font-medium transition ${active === "deliverables" ? "text-[#00E5FF]" : "text-gray-300 hover:text-white"}`}
                    data-testid="nav-deliverables"
                    title="Admin deliverables console"
                  >
                    Deliverables Ops
                  </Link>
                  <Link
                    to="/studio"
                    className={`text-sm font-medium transition ${active === "studio" ? "text-[#FFCC00]" : "text-gray-300 hover:text-[#FFCC00]"}`}
                    data-testid="nav-studio"
                    title="Admin-only CAD workspace"
                  >
                    Studio
                  </Link>
                  <Link
                    to="/admin/pe-approvals"
                    className={`text-sm font-medium transition ${active === "pe-approvals" ? "text-[#FFCC00]" : "text-gray-300 hover:text-[#FFCC00]"}`}
                    data-testid="nav-pe-approvals"
                  >
                    Approvals
                  </Link>
                  <Link
                    to="/admin/pe-licenses"
                    className={`text-sm font-medium transition ${active === "admin-licenses" ? "text-[#FFCC00]" : "text-gray-300 hover:text-[#FFCC00]"}`}
                    data-testid="nav-admin-licenses"
                  >
                    Licenses
                  </Link>
                  <Link
                    to="/admin/pe-analytics"
                    className={`text-sm font-medium transition ${active === "admin-analytics" ? "text-[#FFCC00]" : "text-gray-300 hover:text-[#FFCC00]"}`}
                    data-testid="nav-admin-analytics"
                    title="PEs ranked by client favorites"
                  >
                    Analytics
                  </Link>
                  <Link
                    to="/admin/visitors"
                    className={`text-sm font-medium transition ${active === "admin-visitors" ? "text-[#FFCC00]" : "text-gray-300 hover:text-[#FFCC00]"}`}
                    data-testid="nav-admin-visitors"
                    title="Visitor counts + geo breakdown"
                  >
                    Visitors
                  </Link>
                  <Link
                    to="/admin/contact"
                    className={`text-sm font-medium transition ${active === "admin-contact" ? "text-[#FFCC00]" : "text-gray-300 hover:text-[#FFCC00]"}`}
                    data-testid="nav-admin-contact"
                    title="Landing 'Get in touch' submissions"
                  >
                    Inbox
                  </Link>
                  <Link
                    to="/admin/refunds"
                    className={`text-sm font-medium transition ${active === "admin-refunds" ? "text-[#FFCC00]" : "text-gray-300 hover:text-[#FFCC00]"}`}
                    data-testid="nav-admin-refunds"
                    title="Refund request queue"
                  >
                    Refunds
                  </Link>
                  <Link
                    to="/admin/users"
                    className={`text-sm font-medium transition ${active === "admin-users" ? "text-[#FFCC00]" : "text-gray-300 hover:text-[#FFCC00]"}`}
                    data-testid="nav-admin-users"
                  >
                    Users
                  </Link>
                  <Link
                    to="/admin/permissions"
                    className={`text-sm font-medium transition ${active === "admin-permissions" ? "text-[#FFCC00]" : "text-gray-300 hover:text-[#FFCC00]"}`}
                    data-testid="nav-admin-permissions"
                    title="Fine-grained sub-admin capabilities"
                  >
                    Permissions
                  </Link>
                  <Link
                    to="/admin/vendor-inquiries"
                    className={`text-sm font-medium transition ${active === "admin-vendors" ? "text-[#FFCC00]" : "text-gray-300 hover:text-[#FFCC00]"}`}
                    data-testid="nav-admin-vendors"
                    title="Send vendor partner-inquiries + check credential readiness"
                  >
                    Vendors
                  </Link>
                  <Link
                    to="/admin/integrations"
                    className={`text-sm font-medium transition ${active === "admin-integrations" ? "text-[#FFCC00]" : "text-gray-300 hover:text-[#FFCC00]"}`}
                    data-testid="nav-admin-integrations"
                    title="Manage encrypted API keys for Stripe, Resend, etc."
                  >
                    Integrations
                  </Link>
                </>
              )}
              <Link
                to="/settings"
                className={`text-sm font-medium transition ${active === "settings" ? "text-[#00E5FF]" : "text-gray-300 hover:text-white"}`}
                data-testid="nav-settings"
              >
                Settings
              </Link>
            </>
          )}
        </nav>

        <div className="flex items-center gap-3">
          {user && <AttentionFeed />}
          {user ? (
            <>
              <div className="hidden sm:flex flex-col items-end leading-none">
                <span className="label-tag">{tabSession ? "Tab session" : "Signed in"}</span>
                <span className="text-sm text-white" data-testid="topbar-user-email">
                  {user.email}
                  {tabSession && <span className="ml-1 text-[9px] text-[#00E5FF]" data-testid="topbar-tab-badge">◉ tab</span>}
                </span>
              </div>
              <button
                onClick={async () => { await logout(); nav("/"); }}
                className="btn-ghost flex items-center gap-2 text-sm py-2"
                data-testid="logout-btn"
              >
                <SignOut size={16} /> Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-ghost text-sm py-2" data-testid="login-btn">Sign in</Link>
              <Link to="/register" className="btn-primary text-sm py-2" data-testid="register-btn">Get started</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
